import type ServiceLevel from './ServiceLevel';

interface JSONProviderAccount {
  is_manifestable: boolean;
  is_shippo_account: boolean;
  is_test: boolean;
  nickname: string;
  object_id: string;
  provider_image_75: string;
  provider_image_200: string;
  provider_name: string;
  provider_token: string;
  service_levels: ServiceLevel[];
}

interface ProviderAccount {
  allServiceLevels: ServiceLevel[];
  isManifestable: boolean;
  isShippoAccount: boolean;
  isTest: boolean;
  nickname: string;
  objectId: string;
  providerImage75: string;
  providerImage200: string;
  providerName: string;
  providerToken: string;
  serviceLevels: ServiceLevel[];
  serviceLevelsByToken: {
    [token: string]: ServiceLevel;
  };
  visibleServiceLevels: ServiceLevel[];
}

interface MappedProviderAccount {
  [token: string]: ProviderAccount[];
}

interface ObjectIdProviderAccountMap {
  [objectId: string]: ProviderAccount;
}

const fromJSON = (jsonProviderAccounts: JSONProviderAccount[]): ProviderAccount[] =>
  jsonProviderAccounts
    .sort((a: JSONProviderAccount, b: JSONProviderAccount) => {
      if (!a?.provider_name || !b?.provider_name) return 0;

      return a.provider_name.localeCompare(b.provider_name);
    })
    .map((jsonProviderAccount: JSONProviderAccount): ProviderAccount => {
      const serviceLevelsByToken: Record<string, ServiceLevel> = {};
      for (const serviceLevel of jsonProviderAccount.service_levels)
        serviceLevelsByToken[serviceLevel.token] = serviceLevel;

      // create the list of all Service Levels, including any "child" Service Levels (country-specific variations, for instance)
      // "Child" Service Levels are rarely needed when showing Service Levels to Users, but may be necessary when pairing Rates to Service Levels
      // as Rates *can* have these Service Levels
      const allServiceLevels = jsonProviderAccount.service_levels
        .sort((a, b) => {
          // Sort the Provider Accounts by name
          if (!a?.name || !b?.name) return 0;

          return a.name.localeCompare(b.name);
        })
        .map((serviceLevel) =>
          // Populate the Provider image on the Service Level using the data from the ProviderAccount
          ({
            ...serviceLevel,
            provider_image_75: jsonProviderAccount.provider_image_75,
            provider_image_200: jsonProviderAccount.provider_image_200,
            provider_name: jsonProviderAccount.provider_name,
          }),
        );

      // create the list of Service Levels without any "child" Service Levels (see above)
      const serviceLevels = allServiceLevels.filter(
        (serviceLevel) => !serviceLevel?.parent_servicelevel,
      );

      // create the list of "visible" Service Levels (where all flagged "is_hidden" are filtered out and "child" service levels are removed)
      // In most case, this is the list that should be used if showing possible Service Levels to a user
      const visibleServiceLevels = serviceLevels.filter((serviceLevel) => !serviceLevel?.is_hidden);

      return {
        allServiceLevels,
        isManifestable: jsonProviderAccount.is_manifestable,
        isShippoAccount: jsonProviderAccount.is_shippo_account,
        isTest: jsonProviderAccount.is_test,
        nickname: jsonProviderAccount.nickname,
        objectId: jsonProviderAccount.object_id,
        providerImage75: jsonProviderAccount.provider_image_75,
        providerImage200: jsonProviderAccount.provider_image_200,
        providerName: jsonProviderAccount.provider_name,
        providerToken: jsonProviderAccount.provider_token,
        serviceLevels,
        serviceLevelsByToken,
        visibleServiceLevels,
      };
    });

export { MappedProviderAccount, ObjectIdProviderAccountMap, fromJSON };

export default ProviderAccount;
